import { useEffect, useState, useCallback } from 'react';
import { 
  Typography, 
  Box, 
  CircularProgress, 
  Alert,
  Tabs,
  Tab,
  Divider,
  Stack,
  Chip
} from '@mui/material';
import DateRangeSelector from '../../../components/DateRangeSelector';
import FilterSelectorWithApp from '../../../components/FilterSelectorWithApp';
import AdcashBuzzvilDashboard from '../../../components/adcash/buzzvil/AdcashBuzzvilDashboard';
import AdcashBuzzvilTotalMetricsTable from '../../../components/adcash/buzzvil/AdcashBuzzvilTotalMetricsTable'; 
import AdcashBuzzvilMediationMetricsTable from '../../../components/adcash/buzzvil/AdcashBuzzvilMediationMetricsTable';
import apiClient from '../../../utils/axiosClient';
import { PAGE_CARD_SX } from '../../../theme/pageCardSx';
import dayjs, { Dayjs } from 'dayjs';
import { 
    AdcashBuzzvilTotalMetrics,
    AdcashBuzzvilMetricsDailyStats,
    AdcashBuzzvilMediationMetrics
} from '../../../types/adcash.types';

export default function AdcashBuzzvil() {
  const [buzzvilTotalMetrics, setBuzzvilTotalMetrics] = useState<AdcashBuzzvilTotalMetrics | null>(null);
  const [buzzvilDailyStats, setBuzzvilDailyStats] = useState<AdcashBuzzvilMetricsDailyStats[]>([]);
  const [buzzvilMediationMetrics, setBuzzvilMediationMetrics] = useState<AdcashBuzzvilMediationMetrics | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [startDate, setStartDate] = useState<Dayjs | null>(dayjs().startOf('month'));
  const [endDate, setEndDate] = useState<Dayjs | null>(dayjs().subtract(0, 'day'));
  const [initialized, setInitialized] = useState<boolean>(false);
  const [selectedApp, setSelectedApp] = useState<string | null>('앱 전체');
  const [appOptions, setAppOptions] = useState<string[]>(['앱 전체']);
  const [activeTab, setActiveTab] = useState<number>(0);

  const fetchFilterOptions = useCallback(() => {
    apiClient
      .get(`/api/v1/stat/adcash/buzzvil/app/list`, {
        params: {
          start_date: startDate?.format('YYYY-MM-DD'),
          end_date: endDate?.format('YYYY-MM-DD')
        }
      })
      .then((response) => {
        setAppOptions(['앱 전체', ...response.data]);
      });
  }, [startDate, endDate]);

  useEffect(() => {
    fetchFilterOptions();
  }, [fetchFilterOptions]);

  useEffect(() => {
    setInitialized(true);
  }, []);

  const fetchAdcashBuzzvilTotalMetrics = useCallback(() => {
    if (!startDate || !endDate) return;
    if (selectedApp === null) return;
    setLoading(true);

    const formattedStartDate = startDate.format('YYYY-MM-DD');
    const formattedEndDate = endDate.format('YYYY-MM-DD');

    const params: Record<string, string> = {
      start_date: formattedStartDate,
      end_date: formattedEndDate,
      ...(selectedApp 
        ? { app: selectedApp === '앱 전체' ? 'all' : selectedApp } 
        : {})
    };

    apiClient.get(`/api/v1/stat/adcash/buzzvil/detail/metrics/total`, {
      params: params
    }).then((response) => {
      setBuzzvilTotalMetrics(response.data);
      setBuzzvilDailyStats(response.data.dailyStats);
      setErrorMessage('');
    }).catch((error) => {
      setErrorMessage('데이터 로드에 실패했습니다. 잠시 후 다시 시도해주세요.');
    }).finally(() => {
      setLoading(false);
    });

  }, [startDate, endDate, selectedApp]);
  
  const fetchAdcashBuzzvilMediationMetrics = useCallback(() => {
    if (!startDate || !endDate) return;
    if (selectedApp === null) return;
    setLoading(true);

    const formattedStartDate = startDate.format('YYYY-MM-DD');
    const formattedEndDate = endDate.format('YYYY-MM-DD');

    const params: Record<string, string> = {
      start_date: formattedStartDate,
      end_date: formattedEndDate,
      ...(selectedApp 
        ? { app: selectedApp === '앱 전체' ? 'all' : selectedApp } 
        : {})
    };

    apiClient.get(`/api/v1/stat/adcash/buzzvil/detail/metrics/mediation`, {
      params: params
    }).then((response) => {
      setBuzzvilMediationMetrics(response.data);
      setBuzzvilDailyStats(response.data.dailyStats);
      setErrorMessage('');
    }).catch((error) => {
      setErrorMessage('데이터 로드에 실패했습니다. 잠시 후 다시 시도해주세요.');
    }).finally(() => {
      setLoading(false);
    });
  }, [startDate, endDate, selectedApp]);

  useEffect(() => {
    if (initialized && startDate && endDate && selectedApp != null) {
      // 현재 탭에 맞는 데이터 로드
      if (activeTab === 0) {
        fetchAdcashBuzzvilTotalMetrics();
      } else if (activeTab === 1) {
        fetchAdcashBuzzvilMediationMetrics();
      }
    }
  }, [
    fetchAdcashBuzzvilTotalMetrics,
    fetchAdcashBuzzvilMediationMetrics,
    initialized,
    startDate,
    endDate,
    selectedApp,
    activeTab
  ]);

  return (
    <Box
      sx={{
        opacity: loading ? 0.3 : 1,
        transition: 'opacity 0.3s ease',
        minHeight: '100vh'
      }}
    >
      <Box
        alignItems={'center'}
        display={'flex'}
      >
        <Box>
          <DateRangeSelector
            startDate={startDate}
            endDate={endDate}
            onStartDateChange={setStartDate}
            onEndDateChange={setEndDate}
            onSearch={() => {
              if (startDate && endDate) {
                // 현재 탭에 맞는 데이터 로드
                if (activeTab === 0) {
                  fetchAdcashBuzzvilTotalMetrics();
                } else if (activeTab === 1) {
                  fetchAdcashBuzzvilMediationMetrics();
                }
              }
            }}
          />
        </Box>
        <Box
          sx={{
            ml: 3
          }}
        >
          <FilterSelectorWithApp
            selectedApp={selectedApp}
            appOptions={appOptions}
            onAppChange={(setApp) => {
              setSelectedApp(setApp);
            }}
            onSearch={() => {
              if (startDate && endDate) {
                // 현재 탭에 맞는 데이터 로드
                if (activeTab === 0) {
                  fetchAdcashBuzzvilTotalMetrics();
                } else if (activeTab === 1) {
                  fetchAdcashBuzzvilMediationMetrics();
                }
              }
            }}
          />
        </Box>
      </Box>
      <Box
        sx={{
          ...PAGE_CARD_SX,
          width: '100%',
          maxWidth: '100%',
        }}
      >
        {/* 앱 선택 (Chip) */}
        <Stack
          direction="row"
          spacing={1}
          sx={{
            overflowX: 'auto',
            pb: 2,
            mb: 1,
            '&::-webkit-scrollbar': { height: 8 },
            '&::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
            '&::-webkit-scrollbar-thumb': { backgroundColor: '#d1d5db', borderRadius: 4 },
            '&::-webkit-scrollbar-thumb:hover': { backgroundColor: '#9ca3af' },
          }}
        >
          {appOptions.map((name) => (
            <Chip
              key={name}
              label={name}
              onClick={() => setSelectedApp(name)}
              sx={{
                cursor: 'pointer',
                fontWeight: selectedApp === name ? 700 : 400,
                backgroundColor: selectedApp === name ? '#0044D2' : '#fff',
                color: selectedApp === name ? '#fff' : '#374151',
                border: `1px solid ${selectedApp === name ? '#0044D2' : '#e5e7eb'}`,
                '&:hover': {
                  backgroundColor: selectedApp === name ? '#0033a0' : '#f3f4f6',
                },
                height: 36,
                fontSize: '0.875rem',
              }}
            />
          ))}
        </Stack>

        <Divider variant="fullWidth" sx={{ borderColor: '#e0e0e0', mb: 2 }} />

        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" height={400}>
            <CircularProgress size={50} sx={{ color: '#0044D2' }} />
            <Typography variant="body1" sx={{ ml: 2 }}>
              데이터 로딩 중...
            </Typography>
          </Box>
        ) : errorMessage ? (
          <Box display="flex" justifyContent="center" alignItems="center" height={400}>
            <Alert severity="error">{errorMessage}</Alert>
          </Box>
        ) : (
          <>
            <AdcashBuzzvilDashboard
              dailyStats={buzzvilDailyStats}
              totalRevenue={buzzvilTotalMetrics?.totalRevenue || 0}
              previousTotalRevenue={buzzvilTotalMetrics?.prevTotalRevenue || 0}
            />

            <Box
              sx={{
                backgroundColor: '#ffffff',
                border: '1px solid #e0e0e0',
                width: '1500px',
                maxWidth: '100%',
                borderRadius: 3,
                p: 2,
                mt: 3
              }}
            >
              <Tabs
                value={activeTab}
                onChange={(_, newValue) => {
                  setActiveTab(newValue);
                  // 탭 변경 시 해당 탭의 데이터 로드
                  if (newValue === 0) {
                    fetchAdcashBuzzvilTotalMetrics();
                  } else if (newValue === 1) {
                    fetchAdcashBuzzvilMediationMetrics();
                  }
                }}
              >
                <Tab value={0} label="전체 현황" />
                <Tab value={1} label="미디에이션 현황" />
              </Tabs>

              {activeTab === 0 && buzzvilTotalMetrics && (
                <AdcashBuzzvilTotalMetricsTable
                  data={buzzvilTotalMetrics}
                />
              )}
              {activeTab === 1 && buzzvilMediationMetrics && (
                <AdcashBuzzvilMediationMetricsTable
                  data={buzzvilMediationMetrics}
                />
              )}
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
}
